import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

export type CopyVariant = "baseline" | "A" | "B";
export type CopyBlock = "hero" | "alerts";
export const AB_KEY = "px_ab_v1";
const BOT_PATTERN = /bot|crawl|spider|slurp|googlebot|bingbot|gptbot|claude|perplexity|facebookexternalhit|preview/i;
let sessionVariant: CopyVariant | undefined;

export const isCopyVariant = (value: unknown): value is CopyVariant => value === "baseline" || value === "A" || value === "B";
export const isCopyBot = () => typeof navigator !== "undefined" && BOT_PATTERN.test(navigator.userAgent);

/** Called only after mount, so source HTML and the initial render remain baseline. */
export function getCopyVariant(): CopyVariant {
  if (typeof window === "undefined" || isCopyBot()) return "baseline";
  const override = new URLSearchParams(window.location.search).get("pxab");
  if (isCopyVariant(override)) {
    sessionVariant = override;
    try { window.localStorage.setItem(AB_KEY, override); } catch { /* session fallback */ }
    return override;
  }
  try {
    const stored = window.localStorage.getItem(AB_KEY);
    if (isCopyVariant(stored)) return sessionVariant = stored;
  } catch { /* session fallback */ }
  if (!sessionVariant) {
    const pick = Math.random();
    sessionVariant = pick < 1 / 3 ? "baseline" : pick < 2 / 3 ? "A" : "B";
  }
  try { window.localStorage.setItem(AB_KEY, sessionVariant); } catch { /* session fallback */ }
  return sessionVariant;
}

type AnalyticsWindow = Window & { gtag?: (...args: unknown[]) => void };
export function trackCopyEvent(event: "ab_impression" | "ab_cta_click", block: CopyBlock, variant: CopyVariant) {
  if (typeof window === "undefined" || isCopyBot()) return;
  const { gtag } = window as AnalyticsWindow;
  if (typeof gtag !== "function") return;
  gtag("set", "user_properties", { px_ab: variant });
  gtag("event", event, { experiment: "copy_v1", block, variant });
}

export function useCopyExperiment(block: CopyBlock) {
  const location = useLocation();
  const [assignment, setAssignment] = useState<{ variant: CopyVariant; view: string }>();
  const loggedView = useRef<string>();
  useEffect(() => {
    setAssignment({ variant: getCopyVariant(), view: location.key });
    // Anchor scrolling is not a new page view.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);
  useEffect(() => {
    if (!assignment || loggedView.current === assignment.view) return;
    loggedView.current = assignment.view;
    trackCopyEvent("ab_impression", block, assignment.variant);
  }, [assignment, block]);
  return { variant: assignment?.variant ?? "baseline", ready: Boolean(assignment), onCtaClick: () => trackCopyEvent("ab_cta_click", block, assignment?.variant ?? "baseline") };
}

export function copyEmbedUrl(currentSrc: string, block: CopyBlock, variant: CopyVariant): string {
  const url = new URL(currentSrc);
  url.searchParams.set("utm_source", block === "hero" ? "peptidex-home" : "peptidex");
  url.searchParams.set("utm_medium", "embed");
  url.searchParams.set("utm_campaign", `${block === "hero" ? "hero" : "alerts"}_${variant}`);
  if (block === "hero") url.searchParams.set("slim", "true");
  return url.toString();
}

export const HERO_TEST_COPY = {
  A: { headline: "Know before the headlines: which peptides are FDA-approved, and which are still in trials.", description: "Plain-English status for 94 peptides and GLP-1 drugs, with a source on every claim.", cta: "Get approval alerts" },
  B: { headline: "Don't get fooled by peptide hype. See the real FDA status, with sources.", description: "What's approved, what's still being tested, and what's only proposed. No sellers, no dosing.", cta: "See the approval tracker" },
};
export const ALERTS_TEST_COPY = {
  A: { description: "Be the first to know when a peptide treatment moves closer to approval. One short email, with the FDA source.", button: "Alert me first" },
  B: { description: "Status changes fast and the news gets it wrong. We email you when the FDA or HHS moves, with the primary source.", button: "Send me the updates" },
};